import { Modal, Box, Typography } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";

type CalculationModalProps = {
  onClose: () => void;
};

export const CalculationModal = ({ onClose }: CalculationModalProps) => (
  <Modal open onClose={onClose} aria-labelledby="calculation-modal-title">
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "100%", md: 800 },
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 4,
        maxHeight: "90vh",
        overflow: "auto",
      }}
    >
      <Typography id="calculation-modal-title" variant="h5" gutterBottom>
        How we calculate your plan
      </Typography>
      <Typography variant="body1">
        We represent your base year emissions as 100 carbon units and perform
        all calculations in relation to those 100 units. Each step of the plan
        results in an increase or decrease in carbon units. The final result of
        your plan - planned reduction vs base year – is then expressed as a
        percentage reduction from your base year emissions. We use these key
        assumptions along the way:
      </Typography>
      <List>
        <ListItem>
          In the business-as-usual (BAU) forecast, scope 1 + 2 emissions
          footprint increases 1:1 with annual business growth. In reality, you
          may find ways to reduce emissions intensity through economies of scale
          as your organization grows. Account for that emissions intensity
          improvement in the Cut Energy Waste solution sections.
        </ListItem>
        <ListItem>
          To help get started on the emissions profile page, we've provided
          example profiles by industry type. We recommend you adjust those
          profiles to match your specific emissions footprint.
        </ListItem>
        <ListItem>
          Purchased electricity will become less carbon-intensive as utilities
          shift towards sources of lower-carbon power generation. We take a
          national average of your target year's regional emissions intensity
          forecasts using Cambium 2023 data from the National Renewable Energy
          Laboratory, with a correction factor applied to make the forecast more
          conservative. Regional grids have varying emissions forecasts that are
          not captured in this broad planner. We can help you build detailed
          site-level plans that use regional grid forecasts.
        </ListItem>
        <ListItem>
          When solutions are applied, they affect that emissions source as
          explained below each slider. We assume all electrification solutions
          are 2x more efficient than the baseline fossil fuel system. The
          specific efficiency or coefficient of performance (COP) improvement
          will vary by system and equipment.
        </ListItem>
        <ListItem>
          Each solution is associated with an investment score represented by a
          $-$$$$$ scale where $ = low marginal abatement cost and $$$$$ = high
          marginal abatement cost. These scores are set using broad market and
          technology analysis. Individual projects will have varying abatement
          cost, and we recommend prioritizing solutions with a lower investment
          score (lower marginal abatement cost) in your emissions reduction
          plan.
        </ListItem>
      </List>
    </Box>
  </Modal>
);
